// ─────────────────────────────────────────────
//  SupervisorRoomStatus — the SUPERVISOR Room Status screen: ALL rooms, one page.
//  (design: docs/specs/2026-09-07-mobile-room-status-design.md §6.4, D8)
//
//  A stack of room cards. The owner explicitly chose the FULL four-phase grid
//  per room — nothing collapsed, nothing hidden, nothing behind a disclosure.
//  Each card: room name, surgeon, Time in / Time out, and all four phases with
//  both time columns and the Acknowledge control. ONE message box at the
//  bottom, shared across the rooms (§7: four separate boxes would hide the one
//  message that matters, in whichever box was not opened).
//
//  ⚠️ PRESENTATIONAL ONLY — renders and delegates, never touches Firestore.
//
//  Props (the wiring contract):
//    { rooms: [{ room, orCase, surgeon, mode }], ageMinutesFor,
//      onAck(roomId, phaseId), busy, messageBox }
//
//  ⚠️ ARGUMENT SHAPE. Every callback on this page is ROOM-SCOPED FIRST, because
//  every row on it belongs to one of several rooms: `onAck(roomId, phaseId)`
//  and, to match it, `ageMinutesFor(roomId, phaseId)`. The provider screen is a
//  single room and takes the phase id alone. Both bindings are made HERE, in
//  one place, and the shared PhaseGrid below only ever sees a function of
//  (phaseId) — so a change of shape is one edit, not a hunt through two pages.
//
//  ── mode is derived PER ROOM, not from role (§6.1) ──
//  A supervisor can be the solo occupant of one of their own rooms. That room
//  arrives with mode "provider", and on THIS page it renders read-only: it has
//  no stamp callbacks to offer (stamping lives on the provider screen), and
//  acknowledging your own stamp is precisely what §6.1 forbids — in a solo case
//  providerId === attendingId and the Firestore rules cannot express "not the
//  same human", so hiding Acknowledge is the only guarantee there is.
//
//  ⚠️ No 100vh. The stack is as tall as the rooms make it and scrolls in the
//  shell; a viewport height would put the last room's last phase under the iOS
//  bottom bars, and horizontal scroll is never the phone answer to a wide row.
// ─────────────────────────────────────────────
import React from "react";

import mobile, { px } from "../../theme/tokens.mobile";
import { PhaseGrid, StatusHeader } from "./roomStatusPieces";

const { color, space, radius, layout } = mobile;

const s = {
  page: {
    width: "100%",
    maxWidth: layout.contentMaxWidth,
    margin: "0 auto",
    padding: px(space.md),
    boxSizing: "border-box",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: px(space.md),
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 900,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: color.text.primary,
  },
  card: {
    background: color.surface.cardGradient,
    border: `1px solid ${color.border.card}`,
    borderRadius: px(radius.lg),
    padding: px(space.md),
    // Load-bearing on a flex column of cards containing names: without it the
    // widest room name sets the width of the whole stack and pushes the ack
    // column off a 390px screen.
    minWidth: 0,
  },
  refusal: { color: color.text.muted, fontSize: 14, lineHeight: 1.4 },
};

export default function SupervisorRoomStatus({
  rooms = [],
  ageMinutesFor,
  onAck,
  busy = false,
  messageBox = null,
}) {
  // An entry with no room cannot be drawn or acknowledged; drop it rather than
  // rendering a nameless card that offers taps against an unknown id.
  const entries = (Array.isArray(rooms) ? rooms : []).filter((e) => e && e.room && e.room.id);

  return (
    <div style={s.page} data-testid="room-status-supervisor">
      <h1 style={s.title}>Room status</h1>

      {entries.length === 0 ? (
        <p style={s.refusal} data-testid="room-status-no-rooms">
          No rooms are assigned to you right now.
        </p>
      ) : (
        entries.map(({ room, orCase, surgeon, mode }) => {
          const c = orCase || {};
          // See the header note: a room this supervisor is running solo renders
          // read-only here, so nobody can acknowledge their own stamp.
          const cardMode = mode === "provider" ? null : "supervisor";
          return (
            <section
              key={room.id}
              style={s.card}
              data-testid={`room-status-card-${room.id}`}
              aria-label={`${room.name} room status`}
            >
              <StatusHeader
                variant="card"
                roomName={room.name}
                surgeon={surgeon}
                timeIn={c.timeIn ?? null}
                timeOut={c.timeOut ?? null}
                mode={cardMode}
              />
              {/* The FULL grid, every room, always (D8). */}
              <PhaseGrid
                phases={c.phases}
                mode={cardMode}
                onAck={(phaseId) => onAck && onAck(room.id, phaseId)}
                ageMinutesFor={
                  typeof ageMinutesFor === "function" ? (phaseId) => ageMinutesFor(room.id, phaseId) : undefined
                }
                busy={busy}
                caption={`${room.name} case phases`}
              />
            </section>
          );
        })
      )}

      {/* ONE box, at the bottom, merged across the rooms (§7). */}
      {messageBox}
    </div>
  );
}
